// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import type { Responder, UpcomingRound } from './types';

type Options = {
  userId: string | null | undefined;
  token: string | null | undefined;
};

type Result = {
  rounds: UpcomingRound[];
  loading: boolean;
  refetch: () => void;
};

const normalizeResponders = (raw: unknown): Responder[] => {
  if (!Array.isArray(raw)) return [];
  const out: Responder[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const rr = r as Record<string, unknown>;
    const playerId = rr.playerId ?? rr.PlayerId;
    if (typeof playerId !== 'string') continue;
    out.push({
      playerId,
      playerName: (rr.playerName ?? rr.PlayerName) as string | undefined,
      rsvpStatus: (rr.rsvpStatus ?? rr.RsvpStatus ?? 'invited') as Responder['rsvpStatus'],
      respondedAtUtc: (rr.respondedAtUtc ?? rr.RespondedAtUtc) as string | undefined,
    });
  }
  return out;
};

const normalizeRounds = (data: unknown): UpcomingRound[] => {
  if (!Array.isArray(data)) return [];
  const out: UpcomingRound[] = [];
  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const roundId = record.roundId ?? record.RoundId;
    const teeTimeUtc = record.teeTimeUtc ?? record.TeeTimeUtc;
    if (typeof roundId !== 'string' || typeof teeTimeUtc !== 'string') continue;
    out.push({
      roundId,
      tourId: (record.tourId ?? record.TourId) as string | undefined,
      teeTimeUtc,
      courseId: (record.courseId ?? record.CourseId) as string | undefined,
      courseName: (record.courseName ?? record.CourseName) as string | undefined,
      holesCount: (record.holesCount ?? record.HolesCount) as number | undefined,
      myRsvpStatus: (record.myRsvpStatus ?? record.MyRsvpStatus) as UpcomingRound['myRsvpStatus'],
      responders: normalizeResponders(record.responders ?? record.Responders),
    });
  }
  return out.sort((a, b) => new Date(a.teeTimeUtc).getTime() - new Date(b.teeTimeUtc).getTime());
};

/**
 * Loads the current player's upcoming scheduled rounds and keeps them fresh: it resolves the
 * player id from the signed-in user, fetches GET /api/rounds/upcoming, and refetches whenever a
 * round.scheduleUpdated or round.rsvpUpdated frame arrives on the shared BFF socket.
 */
export function useUpcomingRounds({ userId, token }: Options): Result {
  const [rounds, setRounds] = useState<UpcomingRound[]>([]);
  const [loading, setLoading] = useState(false);
  const playerIdRef = useRef<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!userId || !token) {
      setRounds([]);
      playerIdRef.current = null;
      return;
    }
    const apiBaseUrl = getApiBaseUrl();
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let playerId = playerIdRef.current;
        if (!playerId) {
          const playerResponse = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!playerResponse.ok) throw new Error('player lookup failed');
          const playerPayload = (await playerResponse.json()) as { id?: string };
          playerId = playerPayload.id ?? null;
          playerIdRef.current = playerId;
        }
        if (!playerId) throw new Error('missing player id');
        const response = await fetch(
          `${apiBaseUrl}/api/rounds/upcoming?playerId=${encodeURIComponent(playerId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`upcoming lookup failed (${response.status})`);
        const data = await response.json();
        if (!cancelled) setRounds(normalizeRounds(data));
      } catch {
        if (!cancelled) setRounds([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, token, reloadKey]);

  useEffect(() => {
    if (!token) return;
    const socket = getSharedBffWebSocket(getApiBaseUrl());
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'round.scheduleUpdated' || type === 'round.rsvpUpdated') {
        refetch();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [token, refetch]);

  return { rounds, loading, refetch };
}
